import { action, computed, makeObservable, observable } from 'mobx';
import { rootStore } from 'src/app';
import i18next from 'src/i18n';
import { base_URL } from 'src/routes';
import { DataType, IDeliverable, IItem, IItemLink,ILog,IMeeting,IMilestone,IQuestion,ItemStatus,ItemType,SpaceType,UserRole, Services } from '../types';
import { Data, Deliverable, Meeting, Milestone, Question, Space, Tag } from './';
import format from "date-fns/format";
import { IconId } from 'src/components';
import { theme } from 'src/config';
import client from 'src/feathers';
import { computedFn } from 'mobx-utils';

export class Item<I extends IItem = IItem> extends Data<I> implements IItem {
  
  public readonly status_colors: {[status: string]: string} = {
    new:      `${theme.color.status.new}`,
    archived: `${theme.color.status.archived}`,
    answered: `${theme.color.status.answered}`,
    critical: `${theme.color.status.critical}`,
    active:   `${theme.color.status.active}`,
  }
  
  public readonly status_codes: {[status: string]: string} = {
    new:      i18next.t("new_status"),
    archived: i18next.t("archived_status"),
    answered: i18next.t("answered_status"),
    critical: i18next.t("critical_status"),
    active:   i18next.t("active_status"),
  }

  public readonly status_icons: {[status: string]: IconId} = {
    new:      "new",
    archived: "checkmark",
    answered: "checkmark",
    critical: "critical",
    active:   "letter_a"
  }

  room_id:  number = null;
  title:    string = null;
  desc:     string = null;
  date:     Date   = null;
  number?:  number = null;
  archived: boolean = null;
  workspace_id:    number = null;
  total_comments?: number = null; // Does not include private comments of users != curUser
  total_files?:    number = null;
  edit_prop?:      "desc" | "title" | null = null;
  conflict?:       string | null = null;

  protected constructor(public readonly data_type: DataType, data: I) {
    // The super constructr takes care of references to the service, validation rules
    // and intialized the base members with mobx decorators
    super(data_type, data);

    // Since Mobx cannot track derived class objects automatically,
    // the decorators have to be provided manually 
    makeObservable(this, {
      room_id:        observable,
      title:          observable,
      desc:           observable,
      date:           observable,
      total_comments: observable,
      edit_prop:      observable,
      total_files:    observable,
      conflict:       observable,
      archived:       observable,
      
      getDisplayPath: computed,
      getEditProp:    computed,
      getRoutingPath: computed,
      getStatus:      computed,
      getStatusIcon:  computed,
      getProjectID:   computed,

      setEditProp: action,
      setConflict: action,
      // updateCommentCount: action,
    });

    // Initialize data
    // While the super constructor will call this once,
    // the default values given to members will overwrite this so it has to be called again here. 
    this.fromData(data);
  }

  /**
   * Converts data loaded from the database to an IItemLink.
   */
  public static parseLink = (data: IItemLink | IItem): IItemLink => {
    return {
      title:   data.title,
      id:      Number(data.id),
      date:    data.date && new Date(data.date),
      number:  Number(data.number),
      link_id: "link_id" in data && Number(data.link_id),
    };
  }

  /*******************************************************************************************************************************************
   * Database access
   ********************************************************************************************************************************************/

  /*******************************************************************************************************************************************
   * Overwrites
   ********************************************************************************************************************************************/
  public static create(data?: IItem): Item<IItem> {
    return new Item(DataType.Item, data || {
      room_id:      rootStore.authStore.getCurRoomID,
      title:        "",
      desc:         "",
      date:         null,
      workspace_id: rootStore.authStore.getCurWorkspaceID,
      archived:     false,
    });
  }

  public static createType(type: ItemType, data?: IItem): Item<IItem> {
    switch(type) {
      case DataType.Question:    return Question.create(data as IQuestion);
      case DataType.Deliverable: return Deliverable.create(data as IDeliverable);
      case DataType.Meeting:     return Meeting.create(data as IMeeting);
      case DataType.Milestone:   return Milestone.create(data as IMilestone);
      default:
        console.error("CreateType does not support", type);
    }
  }

  protected fromData(data: I): void {
    super.fromData(data);
    this.room_id      = Number(data.room_id);
    this.title        = data.title;
    this.desc         = data.desc;
    this.total_comments = Number(data.total_comments||0);
    this.total_files  = Number(data.total_files||0);
    
    const date        = data.date ? new Date(data.date).toISOString() : null;
    this.date         = data.date && new Date(date);

    this.number       = Number(data.number);
    this.workspace_id = Number(data.workspace_id);
    this.archived     = data.archived;
  }

  public toData(): any {
    return {
        ...super.toData(),
        archived:     this.archived,
        date:         this.date && this.date.toISOString(),
        desc:         this.desc,
        number:       this.number,
        room_id:      this.room_id,
        title:        this.title,
        workspace_id: this.workspace_id,
    };
  }

  public toPatchData(patch: Partial<I>): any {
    return {
      ...super.toPatchData(patch),
      room_id:      "room_id" in patch ? patch.room_id : undefined,
      title:        "title" in patch ? patch.title : undefined,
      desc:         "desc" in patch ? patch.desc : undefined,
      date:         "date" in patch ? patch.date && patch.date.toISOString() : undefined,
      number:       "number" in patch ? patch.number : undefined,
      workspace_id: "workspace_id" in patch ? patch.workspace_id : undefined,
      archived:     "archived" in patch ? patch.archived : undefined,
    };
  }

  /**
   * @return <true> if the currently logged in user has the rights to edit this item
   */
  public get canEdit() : boolean {
    return !this.isNew && !this.archived;
  }

  public canEditField = computedFn((field?: keyof I): boolean => {
    return this.canEdit
  });

  /*******************************************************************************************************************************************
   * Getter 
   ********************************************************************************************************************************************/

  get getStatus(): ItemStatus {
    return ItemStatus.New;
  }

  get getPosition(): string {
    return ItemStatus.New;
  }

  get getStatusColor(): string {
    return this.status_colors[this.getStatus];
  }
  
  get getStatusCode(): string {
    return this.status_codes[this.getStatus];
  }

  get getStatusIcon(): IconId {
    return this.status_icons[this.getStatus];
  }

  get isOverDueDate(): boolean {
    const formattedTodayDate = format(new Date(), "MM/dd/yyyy");
    const formattedDueDate = format(new Date(this.date), "MM/dd/yyyy");
    return formattedTodayDate > formattedDueDate;
  }

  /**
   * @return item path <project>/<room>/?<subroom>/
   */
  get getDisplayPath(): string {
    const room:   Space = rootStore.spaceStore.getItemByID(this.room_id);
    if(!room) return '';
    
    const parent: Space = rootStore.spaceStore.getItemByID(room.parent);
    
    const spaceTitles: string[] = [];
    if(parent.type === SpaceType.Room) { // Item is in a subroom
      spaceTitles.push(rootStore.spaceStore.getItemByID(parent.parent).title);
    }
    spaceTitles.push(parent.title);
    spaceTitles.push(room.title);

    return spaceTitles.join(" > ");
  }


  /**
   * @return name of room
   */
  get getRoomName(): string | undefined {
    const room:   Space = rootStore.spaceStore.getItemByID(this.room_id);
    return room?.title;
  }

  /**
   * @return name of project
   */
  get getProjectName(): string | undefined {
    const room:   Space = rootStore.spaceStore.getItemByID(this.room_id);
    if (!room) return undefined;

    const project: Space = rootStore.spaceStore.getItemByID(room.project_id);
    return project?.title
  }

  get getProjectID(): number {
    return rootStore.spaceStore.getItemByID(this.room_id)?.project_id;
  }

  /**
   * @return routing path to directly open this item <baselink>/room/:room_id/question/:question_id
   */
  get getRoutingPath(): string {
    return `${base_URL}/room/${this.room_id}/${this.data_type}/${this.id}`;
  }
  
  get getTotalComments(): number {
    return this.total_comments || 0;
  }

  get getEditProp(): "desc" | "title" | null {
    return this.edit_prop;
  }

  /*******************************************************************************************************************************************
   * Setter 
   ********************************************************************************************************************************************/
  setEditProp = (prop: "desc" | "title" | null): void => {
    this.edit_prop = prop;
  }

  setConflict = (val: string): void => {
    this.conflict = val;
  }

  /*******************************************************************************************************************************************
   * Actions
   ********************************************************************************************************************************************/
  /**
   * Creates an ICS file from the current meeting and offers it to the user to download it
   */
  public icsExport = async (): Promise<void> => {
    try {
      const file = await client.service(Services.ICS).create({
        id: this.id,
        workspace_id: this.workspace_id,
        room_id: this.room_id,
      }) as {error: string, value: string};
      if(!file) return;
      const element = document.createElement('a');
      element.setAttribute('href', 'data:text/calendar;charset=utf-8,' + encodeURIComponent(file.value));
      element.setAttribute('download', this.title + '.ics');
      element.style.display = 'none';
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    } catch (error) {
      console.error('Fail to generate meeting ics', error)
    }
  }

  public archive = async (): Promise<void> => {
    try {
      await this.set("archived", true, true);
    } catch (err) {
      console.error("Failed to archived item", err);
    }
  }

  public async validate (asBoolean?: boolean): Promise<any> {
    if(this.isNew)    return [];
    if(this.archived) return [];
    
    // type this object, make generic for items and let each items contribute to list of required fields
    const obj: any = {
      title: this.title,
      date: this.date,
    };
    
    const invalids = Object.keys(obj).filter((k) => {
      return !obj[k];
    });

    return asBoolean ? invalids.length : invalids;
  }

}
